import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Phone, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import { motion } from 'framer-motion';

const ThankYouPage = () => {
  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Thank You - We'll Be In Touch Soon"
        description="Thank you for contacting Navigating Minds Psychology. We've received your enquiry and will be in touch soon."
        url="/thank-you"
      />

      <section className="min-h-screen flex items-center justify-center py-16" style={{ backgroundColor: 'var(--warm-white)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.6 }}
              className="inline-flex items-center justify-center w-24 h-24 bg-[var(--success)] rounded-full mb-8"
            >
              <CheckCircle className="w-12 h-12 text-white" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                Thank You!
              </h1>
              <p className="text-xl mb-8" style={{ color: 'var(--text-secondary)' }}>
                We've received your message and will be in touch within 1-2 business days.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white p-8 rounded-2xl shadow-lg mb-8"
            >
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                What Happens Next?
              </h2>
              <ul className="space-y-4 text-left" style={{ color: 'var(--text-secondary)' }}>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-[var(--blue-pale)] rounded-full flex items-center justify-center mr-3 mt-1" style={{ color: 'var(--navy-primary)' }}>
                    1
                  </span>
                  <div>
                    <strong>Review:</strong> Our team will review your enquiry and match you with the most appropriate psychologist.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-[var(--blue-pale)] rounded-full flex items-center justify-center mr-3 mt-1" style={{ color: 'var(--navy-primary)' }}>
                    2
                  </span>
                  <div>
                    <strong>Contact:</strong> We'll reach out via your preferred contact method to discuss your needs and available appointments.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-[var(--blue-pale)] rounded-full flex items-center justify-center mr-3 mt-1" style={{ color: 'var(--navy-primary)' }}>
                    3
                  </span>
                  <div>
                    <strong>Book:</strong> We'll schedule your first appointment at a time that works for you.
                  </div>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="bg-[var(--gray-100)] p-6 rounded-xl mb-8">
                <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>
                  Need immediate assistance or have urgent questions?
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="tel:+61721503636"
                    className="inline-flex items-center justify-center px-6 py-3 bg-[var(--blue-primary)] text-white rounded-full font-medium hover:bg-[var(--navy-primary)] transition-all duration-300"
                  >
                    <Phone size={18} className="mr-2" />
                    07 2150 3636
                  </a>
                  <a
                    href="mailto:admin@navigatingminds.com.au"
                    className="inline-flex items-center justify-center px-6 py-3 bg-white border-2 border-[var(--blue-primary)] rounded-full font-medium hover:bg-[var(--gray-100)] transition-all duration-300"
                    style={{ color: 'var(--blue-primary)' }}
                  >
                    <Mail size={18} className="mr-2" />
                    Email Us
                  </a>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/"
                  data-testid="thankyou-home-link"
                  className="inline-flex items-center justify-center px-8 py-4 bg-[var(--blue-primary)] text-white rounded-full font-medium hover:bg-[var(--navy-primary)] transition-all duration-300 hover:scale-105"
                >
                  Return Home
                  <ArrowRight size={18} className="ml-2" />
                </Link>
                <Link
                  to="/services"
                  data-testid="thankyou-services-link"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white border-2 border-[var(--blue-primary)] rounded-full font-medium hover:bg-[var(--gray-100)] transition-all duration-300"
                  style={{ color: 'var(--blue-primary)' }}
                >
                  View Our Services
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ThankYouPage;